<script setup lang="ts">
import CornerLogo from '@/components/CornerLogo.vue';
import Footer from '@/components/Footer.vue';
import Header from '@/components/Header.vue';
import ImageCarousel from '@/components/ImageCarousel.vue';
import Price from '@/components/Price.vue';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { watchOverlayBool } from '@/composables/overlays.ts';
import { useSidebar } from '@/composables/sidebar.ts';
import { useCurrency } from '@/stores/currency';
import {
  DEFAULT_OPTION_TITLE,
  DEFAULT_OPTION_VALUE_TITLE,
  type Product,
  type VariantByOptions,
  getVariantByOptions,
  decodeVariantAvailability,
  isVariantAvailable,
  getProductById,
} from '@/lib/shopify/product.ts';
import { sendPlausible } from '@/lib/utils';
import { useShopify } from '@/stores/shopify.ts';
import { VisuallyHidden } from 'radix-vue';
import { computed, ref, watch } from 'vue';

const props = defineProps<{
  product: Product;
}>();

const shopify = useShopify();
const currencyStore = useCurrency();
const currentProduct = ref<Product>(props.product);

type SelectedOption = {
  optionIndex: number;
  valueIndex: number;
  name: string;
};

const selectedOptions = ref<Map<string, SelectedOption>>(new Map());
const calculatedVariant = ref<VariantByOptions | null>(null);

const loadingPrice = ref(true);

// Watch for currency changes and refetch product data
watch(
  [() => currencyStore.selectedCurrency, () => currencyStore.currencyChanged],
  async ([currency, changed], [oldCurrency, oldChanged]) => {
    if (!currentProduct.value?.id) {
      return;
    }
    
    loadingPrice.value = true;
    try {
      const updatedProduct = await getProductById(currentProduct.value.id);
      if (updatedProduct) {
        currentProduct.value = updatedProduct;

        // Re-fetch variant data if we have selected options
        if (selectedOptions.value.size > 0) {
          const options = Array.from(selectedOptions.value.entries()).map(([name, { name: value }]) => ({ name, value }));
          calculatedVariant.value = await getVariantByOptions(currentProduct.value.id, options);
        }
      }
    } catch (error) {
      // Silently fail
    }
    loadingPrice.value = false;
  },
  { immediate: true }
);

watch(
  selectedOptions,
  async () => {
    loadingPrice.value = true;

    if (currentProduct.value.options.length === selectedOptions.value.size) {
      const options = Array.from(selectedOptions.value.entries()).map(([name, { name: value }]) => ({ name, value }));

      calculatedVariant.value = await getVariantByOptions(currentProduct.value.id, options);
    }

    loadingPrice.value = false;
  },
  { deep: true },
);

const variantAvailability = ref<number[][]>([]);
const variantExistence = ref<number[][]>([]);

function onProductChange() {
  loadingPrice.value = true;
  selectedOptions.value = new Map();

  variantAvailability.value = decodeVariantAvailability(currentProduct.value.encodedVariantAvailability ?? '');
  variantExistence.value = decodeVariantAvailability(currentProduct.value.encodedVariantExistence ?? '');

  if (currentProduct.value.options.length > 0) {
    for (let optionIndex = 0; optionIndex < currentProduct.value.options.length; optionIndex++) {
      const { name: optionName, optionValues } = currentProduct.value.options[optionIndex];
      const { name: valueName } = optionValues[0] || { name: null };

      if (valueName === null) {
        continue;
      }

      selectedOptions.value.set(optionName, { optionIndex, valueIndex: 0, name: valueName });
    }
  }

  loadingPrice.value = false;
}

watch(() => currentProduct.value, onProductChange, { immediate: true });

const addingToCart = ref(false);

const sidebarOpen = useSidebar();

async function addToCart() {
  addingToCart.value = true;

  if (!calculatedVariant.value) {
    addingToCart.value = false;
    return;
  }

  shopify.addToCart(calculatedVariant.value.id, 1);

  addingToCart.value = false;

  sidebarOpen.value = true;

  sendPlausible('Added to cart', {
    props: {
      productId: currentProduct.value.id,
      product: currentProduct.value.title,
      variantId: calculatedVariant.value.id,
      variant: calculatedVariant.value.title,
    },
  });
}

const isSale = computed(() => {
  const compareAt = calculatedVariant.value?.compareAtPrice?.amount ?? null;
  const base = calculatedVariant.value?.price.amount ?? null;

  if (compareAt === null || base === null) {
    return false;
  }

  return Number.parseFloat(base) < Number.parseFloat(compareAt);
});

const hasOptions = computed(() => {
  if (currentProduct.value.options.length === 0) {
    return false;
  }

  const firstOption = currentProduct.value.options[0];

  return firstOption.name !== DEFAULT_OPTION_TITLE && firstOption.optionValues[0].name !== DEFAULT_OPTION_VALUE_TITLE;
});

const dialogImageIndex = ref(0);
const dialogOpen = ref(false);

function imageClicked(index: number) {
  dialogOpen.value = true;
  dialogImageIndex.value = index;
}

watchOverlayBool(dialogOpen);

function selectedVariantAvailable(selectedOptions: Map<string, SelectedOption>) {
  const combined = [];

  for (const selected of selectedOptions.values()) {
    combined[selected.optionIndex] = selected.valueIndex;
  }

  const available = isVariantAvailable(variantAvailability.value, combined);

  return available;
}

function optionExists(selectedOptions: Map<string, SelectedOption>, optionIndex: number, valueIndex: number) {
  const combined = [];

  for (const selected of selectedOptions.values()) {
    combined[selected.optionIndex] = selected.valueIndex;
  }

  combined[optionIndex] = valueIndex;

  const exists = isVariantAvailable(variantExistence.value, combined);

  return exists;
}

const outOfStock = computed(() => !selectedVariantAvailable(selectedOptions.value));
</script>

<template>
  <main class="p-4 flex flex-col gap-4">
    <Header />

    <section
      v-if="currentProduct"
      class="relative bg-primary rounded-lg p-8 pt-24 pb-16 flex justify-center col-span-12 min-h-[600px]"
    >
      <CornerLogo class="hidden md:block" />

      <div class="flex flex-col md:grid md:grid-cols-12 gap-12 max-w-7xl w-full">
        <div class="flex flex-col col-span-7 w-full">
          <div class="mb-8">
            <ImageCarousel :images="currentProduct.images.nodes" @click="imageClicked" />
          </div>
        </div>

        <Dialog :open="dialogOpen" @update:open="(v) => (dialogOpen = v)">
          <VisuallyHidden>
            <DialogTitle>Product Image</DialogTitle>
            <DialogDescription>View a larger version of the product image.</DialogDescription>
          </VisuallyHidden>
          <DialogContent class="carousel-dialog">
            <ImageCarousel :images="currentProduct.images.nodes" v-model="dialogImageIndex" />
          </DialogContent>
        </Dialog>

        <div class="flex flex-col col-span-5 w-full">
          <div class="flex flex-col gap-4">
            <div class="space-y-1">
              <h1 class="text-4xl font-extrabold uppercase tracking-wide">{{ currentProduct.title }}</h1>
              <div class="flex flex-col">
                <span class="text-xl font-bold flex gap-2 items-end" :class="{ 'text-neutral-500': loadingPrice }">
                  <template v-if="isSale">
                    <span class="text-2xl text-black"><Price :value="calculatedVariant?.price" /></span>
                    <span class="line-through text-gray-500 text-lg font-normal">
                      <Price :value="calculatedVariant?.compareAtPrice" />
                    </span>
                  </template>
                  <span v-else class="text-2xl text-black"><Price :value="calculatedVariant?.price" /></span>
                </span>
                <slot name="discount" />
              </div>
            </div>

            <div v-if="hasOptions" class="flex flex-col gap-2 mt-2">
              <div v-for="(option, i) in currentProduct.options" :key="option.id">
                <Label class="font-extrabold text-lg tracking-wide">{{ option.name }}</Label>
                <div class="mt-1 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-1 lg:grid-cols-2 gap-2">
                  <Button
                    v-for="(value, j) in option.optionValues"
                    :key="value.id"
                    variant="outline"
                    size="lg"
                    class="text-lg flex-[1_0_auto]"
                    :class="{ 'bg-black text-white hover:bg-black': selectedOptions.get(option.name)?.valueIndex === j }"
                    @click="
                      selectedOptions.set(option.name, {
                        optionIndex: i,
                        valueIndex: j,
                        name: value.name,
                      })
                    "
                    :disabled="!optionExists(selectedOptions, i, j)"
                  >
                    <span>{{ value.name }}</span>
                  </Button>
                </div>
              </div>
            </div>

            <Button :disabled="addingToCart || outOfStock" class="text-xl" variant="accent" size="xl" @click="addToCart">
              <span v-if="!outOfStock">Order Now</span>
              <span v-else>Out of Stock</span>
            </Button>

            <div class="flex flex-col gap-4">
              <slot name="product-details" :variant="calculatedVariant" />

              <div v-if="!$slots?.default" class="product-desc text-content !p-0" v-html="currentProduct.descriptionHtml"></div>
            </div>
          </div>
        </div>
      </div>
    </section>
    
    <div class="grid grid-cols-12 gap-8 mt-4">
      <slot />
    </div>

    <Footer />
  </main>
</template>

<style>
.product-desc {
  @apply flex flex-col gap-2 font-chakra;

  h1 {
    @apply text-2xl font-bold mt-4;
  }

  h2 {
    @apply text-xl font-bold mt-4;
  }
}

.carousel-dialog {
  @apply w-full h-full;

  > .carousel {
    @apply w-full;

    .carousel-content-inner {
      @apply h-full;
    }

    img {
      @apply w-full object-contain bg-black;
    }
  }
}

.carousel-dialog,
.carousel-dialog > * {
  @apply max-w-[calc(100vw-3rem)] max-h-[calc(100svh-3rem)] md:max-w-[calc(100vw-6rem)] md:max-h-[calc(100svh-6rem)];
}
</style>
